import type { QuestionProps } from '../eform.types'
import CalendarDatePicker, { todayIsoDate } from './CalendarDatePicker'

/**
 * Birth dates want a different range and a different year ordering than every other
 * date on a form: 120 years back, nothing in the future, newest year first. Nothing
 * in the legacy question record marks a field as a birth date, so it comes from the
 * question text, the same way TextQuestion picks phone and email fields apart.
 */
export function isBirthDateQuestion(questionContent: string): boolean {
  const plain = (questionContent || '').replace(/<[^>]*>/g, ' ').replace(/\./g, '')
  return /\bbirth\w*\b/i.test(plain) || /\bdob\b/i.test(plain)
}

export default function DateField({ question, value, onChange, error }: QuestionProps) {
  const strValue = Array.isArray(value) ? value[0] ?? '' : value

  const isBirthDate = isBirthDateQuestion(question.questionContent)
  const currentYear = new Date().getFullYear()

  // Legacy eForms carry no min/max per question, so the range is a wide, sane default:
  // a birth date cannot be in the future, and any other date gets a century back and a
  // decade forward, which covers past history and scheduling alike.
  const min = `${currentYear - (isBirthDate ? 120 : 100)}-01-01`
  const max = isBirthDate ? todayIsoDate() : `${currentYear + 10}-12-31`

  return (
    <div>
      <CalendarDatePicker
        value={strValue}
        onChange={onChange}
        min={min}
        max={max}
        yearOrder={isBirthDate ? 'desc' : 'asc'}
        size="lg"
        invalid={!!error}
        ariaLabel={
          (question.questionContent || '').replace(/<[^>]*>/g, ' ').trim() ||
          (isBirthDate ? 'Date of birth' : 'Date')
        }
      />
      {error && <p className="text-sm text-red-500 mt-1">{error}</p>}
    </div>
  )
}
